import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import {
  createConversationMessage,
  listConversationMessages,
  listConversationMessagesForQuery,
  type CoreConversationMessage,
} from '@/lib/coreCommunication';
import { findClientAiConversationId } from '@/lib/coreClientChat';
import Modal from '@/pages/dashboard/core/components/Modal';

interface AiClientThreadModalProps {
  /** Organisation (Socle) à laquelle rattacher la réponse du marchand. */
  organizationId: number;
  /** Tenant Core de l'organisation. */
  tenantId: number | null;
  /** Id utilisateur du client dont on veut rouvrir le fil IA. */
  clientId: number;
  /** Rappel du contexte (ex : le message de la notification). */
  contextLabel?: string | null;
  /**
   * `full` : fil complet (Communication du Socle).
   * `scoped` : uniquement la demande qui concerne l'offre du marchand —
   * le reste de la conversation privée du client n'est jamais chargé.
   */
  mode?: 'full' | 'scoped';
  /** Nom de l'offre concernée (produit / service / boutique). */
  itemName?: string | null;
  /** Type de l'offre concernée. */
  itemType?: string | null;
  onClose: () => void;
  /** Appelé après l'envoi d'une réponse (pour rafraîchir une liste). */
  onReplied?: () => void;
}

interface ClientIdentity {
  name: string | null;
  userName: string | null;
  ville: string | null;
  image: string | null;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function offerTypeLabel(type: string | null | undefined): string {
  if (type === 'produit') return 'Produit';
  if (type === 'service') return 'Service';
  if (type === 'boutique') return 'Boutique';
  return 'Offre';
}

function offerTypeIcon(type: string | null | undefined): string {
  if (type === 'produit') return 'ri-shopping-bag-3-line';
  if (type === 'service') return 'ri-service-line';
  if (type === 'boutique') return 'ri-store-2-line';
  return 'ri-price-tag-3-line';
}

/**
 * Affiche un fil de discussion IA client et permet au marchand d'y répondre.
 *
 * Deux usages :
 *  - `full` (Communication du Socle) : fil complet ;
 *  - `scoped` (notification « Recherche client IA ») : SEULEMENT la demande
 *    qui concerne l'offre du marchand. Le reste de la conversation du client
 *    n'est ni chargé ni affiché — la vie privée du client est préservée.
 */
export default function AiClientThreadModal({
  organizationId,
  tenantId,
  clientId,
  contextLabel,
  mode = 'full',
  itemName,
  itemType,
  onClose,
  onReplied,
}: AiClientThreadModalProps) {
  const { user } = useAuth();
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<CoreConversationMessage[]>([]);
  const [client, setClient] = useState<ClientIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const scoped = mode === 'scoped';

  const loadIdentity = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('users')
        .select('id, name, user_name, Ville, image')
        .eq('id', clientId)
        .maybeSingle();
      if (data) {
        const row = data as { name: string | null; user_name: string | null; Ville: string | null; image: string | null };
        setClient({
          name: row.name || row.user_name || null,
          userName: row.user_name || null,
          ville: row.Ville || null,
          image: row.image || null,
        });
      }
    } catch {
      /* best-effort */
    }
  }, [clientId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const convId = await findClientAiConversationId(clientId);
      setConversationId(convId);
      if (!convId) {
        setMessages([]);
        return;
      }
      if (scoped) {
        // 🔒 Vue ciblée : uniquement les messages qui correspondent à la
        // demande reçue. Aucun autre message du client n'est récupéré.
        setMessages(await listConversationMessagesForQuery(convId, contextLabel, 4));
      } else {
        setMessages(await listConversationMessages(convId));
      }
    } catch {
      setConversationId(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, contextLabel, scoped]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadIdentity(); }, [loadIdentity]);

  const send = async () => {
    if (!conversationId || !draft.trim()) return;
    setSending(true);
    try {
      await createConversationMessage({
        conversationId,
        tenantId,
        organizationId,
        channel: 'ai_chat',
        direction: 'outbound',
        authorType: 'agent',
        authorUserId: user?.id ?? null,
        authorName: user?.name || 'Vous',
        body: draft.trim(),
      });
      if (!scoped) setMessages(await listConversationMessages(conversationId));
      setDraft('');
      setSent(true);
      onReplied?.();
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={scoped ? 'Demande liée à votre offre' : 'Conversation IA du client'}
      onClose={onClose}
      maxWidth="max-w-2xl"
      footer={
        conversationId ? (
          <div className="flex items-center gap-2 w-full">
            <input
              className={INPUT}
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setSent(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={scoped ? 'Répondre à ce client…' : 'Répondre au client…'}
            />
            <button onClick={send} disabled={sending || !draft.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
              <i className="ri-send-plane-line"></i> Envoyer
            </button>
          </div>
        ) : (
          <span className="text-xs text-foreground-400">Aucun fil enregistré pour ce client.</span>
        )
      }
    >
      {/* Fiche client (nom + ville uniquement) */}
      {(client || scoped) && (
        <div className="flex items-center gap-3 p-3 mb-3 rounded-lg bg-background-100/70 border border-background-200/60">
          <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {client?.image
              ? <img src={client.image} alt="" className="w-full h-full object-cover" />
              : <i className="ri-user-3-line"></i>}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground-900 truncate">{client?.name || `Client #${clientId}`}</p>
            <p className="text-[11px] text-foreground-500 truncate">
              {client?.ville ? <><i className="ri-map-pin-line mr-1"></i>{client.ville}</> : 'Client Zifek'}
            </p>
          </div>
          {scoped && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap flex-shrink-0">
              Demande ciblée
            </span>
          )}
        </div>
      )}

      {/* Offre concernée */}
      {scoped && (itemName || itemType) && (
        <div className="flex items-center gap-3 p-3 mb-3 rounded-lg bg-accent-50 border border-accent-100">
          <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className={offerTypeIcon(itemType)}></i>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-accent-700">Votre offre concernée</p>
            <p className="text-sm font-semibold text-foreground-900 truncate">{itemName || offerTypeLabel(itemType)}</p>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-50 text-foreground-600 whitespace-nowrap flex-shrink-0">
            {offerTypeLabel(itemType)}
          </span>
        </div>
      )}

      {contextLabel && (
        <div className="space-y-1.5 mb-3 p-3 bg-background-50 border border-background-200/70 rounded-md">
          <p className="text-[11px] font-semibold text-foreground-600 flex items-center gap-1.5">
            <i className="ri-chat-quote-line text-accent-600"></i>
            {scoped ? 'Ce que le client a demandé' : 'Demande du client'}
          </p>
          <p className="text-xs text-foreground-700 whitespace-pre-wrap">{contextLabel}</p>
        </div>
      )}

      {scoped && (
        <div className="flex items-start gap-2 p-2.5 mb-3 rounded-md bg-background-100/70 border border-background-200/50">
          <i className="ri-shield-check-line text-primary-600 text-sm mt-0.5 flex-shrink-0"></i>
          <p className="text-[11px] text-foreground-500 leading-relaxed">
            Vous voyez uniquement la demande qui concerne votre offre et les messages du client qui s'y rapportent.
            Le reste de sa conversation avec l'assistant reste privé.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : scoped ? (
        messages.length === 0 ? (
          <p className="text-xs text-foreground-400 text-center py-5">
            Aucun message du client ne correspond à cette demande. Utilisez le champ ci-dessous pour le contacter.
          </p>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => (
              <div key={m.id} className="rounded-lg px-3 py-2 bg-background-100 text-foreground-800">
                <p className="text-[11px] font-semibold text-foreground-500 mb-0.5 flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {m.author_name || client?.name || 'Client'}
                </p>
                <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                <p className="text-[10px] text-foreground-400 mt-1">
                  {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            ))}
          </div>
        )
      ) : messages.length === 0 ? (
        <p className="text-xs text-foreground-400 text-center py-6">Aucun message dans ce fil.</p>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => {
            const mine = m.author_type === 'agent';
            const isAi = m.author_type === 'ai';
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? 'bg-primary-500 text-background-50' : isAi ? 'bg-accent-50 text-foreground-800' : 'bg-background-100 text-foreground-800'}`}>
                  <p className="text-xs font-semibold opacity-80 mb-0.5 flex items-center gap-1">
                    {isAi && <i className="ri-robot-2-line"></i>}
                    {m.author_name || (mine ? 'Vous' : isAi ? 'Assistant IA' : 'Client')}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  <p className={`text-[10px] mt-1 ${mine ? 'text-background-100/80' : 'text-foreground-400'}`}>
                    {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sent && (
        <p className="mt-3 text-[11px] text-primary-700 bg-primary-50 border border-primary-100 rounded-md px-3 py-2 flex items-center gap-1.5">
          <i className="ri-check-double-line"></i>
          Réponse envoyée au client dans son fil.
        </p>
      )}
    </Modal>
  );
}